/**
 * RefreshHistoryTab - Display refresh history
 *
 * Shows past refresh executions with timing and status information.
 */

import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { History, Loader2, AlertCircle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { useViewRefreshHistory } from '@/hooks/useViews';
import type { ViewRefreshHistory } from '@/types/views';
import { formatDuration } from '@/utils/formatDuration';

interface RefreshHistoryTabProps {
  viewId: string;
}

/**
 * RefreshHistoryTab Component
 */
export const RefreshHistoryTab: React.FC<RefreshHistoryTabProps> = ({ viewId }) => {
  const { data: history, isLoading, error } = useViewRefreshHistory(viewId);

  const columns = useMemo<ColumnDef<ViewRefreshHistory>[]>(
    () => [
      {
        accessorKey: 'id',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Refresh ID</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.id;
          if (!value) return null;
          return (
            <code className="text-xs bg-muted px-2 py-1 rounded">
              {value.substring(0, 8)}...
            </code>
          );
        },
      },
      {
        accessorKey: 'started_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Started At</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.started_at;
          if (!value) return <span className="text-muted-foreground">-</span>;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm:ss');
          } catch {
            return value;
          }
        },
      },
      {
        accessorKey: 'duration_ms',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Duration</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.duration_ms;
          if (value === undefined || value === null) {
            return <span className="text-muted-foreground">-</span>;
          }
          return formatDuration(value / 1000);
        },
        sortingFn: (a, b) => (a.original.duration_ms ?? 0) - (b.original.duration_ms ?? 0),
      },
      {
        accessorKey: 'success',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Status</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const success = row.original.success;
          if (success === undefined || success === null) return null;
          if (success) {
            return (
              <Badge variant="outline" className="bg-success/10 text-success border-success/30">
                Completed
              </Badge>
            );
          }
          return (
            <Badge
              variant="outline"
              className="bg-destructive/10 text-destructive border-destructive/30"
            >
              Failed
            </Badge>
          );
        },
      },
      {
        accessorKey: 'files_processed',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Files</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.files_processed;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return value.toLocaleString();
        },
        sortingFn: (a, b) =>
          (a.original.files_processed ?? 0) - (b.original.files_processed ?? 0),
      },
      {
        accessorKey: 'files_added',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Added</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.files_added;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return value.toLocaleString();
        },
        sortingFn: (a, b) => (a.original.files_added ?? 0) - (b.original.files_added ?? 0),
      },
      {
        accessorKey: 'files_removed',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Removed</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.files_removed;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return value.toLocaleString();
        },
        sortingFn: (a, b) => (a.original.files_removed ?? 0) - (b.original.files_removed ?? 0),
      },
      {
        accessorKey: 'trigger_type',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Trigger</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.trigger_type;
          if (!value) return <span className="text-muted-foreground">-</span>;
          const colors: Record<string, string> = {
            manual: 'bg-info/10 text-info border-info/30',
            scheduled: 'bg-violet/10 text-violet border-violet/30',
            ingestion: 'bg-muted text-muted-foreground',
            materialization: 'bg-muted text-muted-foreground',
          };
          return (
            <Badge variant="outline" className={colors[value] || ''}>
              {value}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'error_message',
        header: 'Error',
        cell: ({ row }) => {
          const value = row.original.error_message;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-destructive text-sm" title={value}>
              {value.length > 50 ? `${value.slice(0, 47)}...` : value}
            </span>
          );
        },
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Loading refresh history...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error.message || 'Failed to load refresh history'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No Refresh History</p>
            <p className="text-sm">This view has not been refreshed yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* History Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Refresh History</CardTitle>
          <CardDescription>{history.length} refresh executions recorded</CardDescription>
        </CardHeader>
      </Card>

      {/* History DataTable */}
      <Card>
        <CardContent className="pt-6">
          <DataTable
            data={history}
            columns={columns}
            pageSize={20}
            initialSorting={[{ id: 'started_at', desc: true }]}
            getRowId={(h) => h.id}
          />
        </CardContent>
      </Card>
    </div>
  );
};
